// src/components/PhoneModel.tsx
import { useRef, useEffect, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function PhoneModel({
  color,
  material,
  buttonColor,
  hasButtons,
  screenBorderColor,
  cameraStyle,
  cameraColor,
  cameraPosition,
  buttonPositions,
  phoneSize,
  screenSize,
  logo
}) {
  const phoneRef = useRef(null);
  const [logoTexture, setLogoTexture] = useState(null);
  useEffect(() => {
    if (logo.type === "image" && logo.url) {
      const loader = new THREE.TextureLoader();
      loader.load(logo.url, (texture) => {
        setLogoTexture(texture);
      });
    } else {
      setLogoTexture(null);
    }
  }, [logo.type, logo.url]);
  useFrame((state) => {
    if (phoneRef.current) {
      phoneRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.3;
    }
  });
  const effectiveCameraColor = cameraColor === "match" ? color : cameraColor;
  const effectiveButtonColor = buttonColor === "match" ? color : buttonColor;
  const screenWidth = phoneSize.width * (screenSize.width / 100);
  const screenHeight = phoneSize.height * (screenSize.height / 100);
  const borderWidth = screenWidth + 0.05;
  const borderHeight = screenHeight + 0.05;
  const cameraMaterial = new THREE.MeshStandardMaterial({
    color: effectiveCameraColor,
    metalness: 0.8,
    roughness: 0.2,
    depthWrite: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -4
  });
  return /* @__PURE__ */ jsxs("group", { ref: phoneRef, position: [0, 0, 0], children: [
    /* @__PURE__ */ jsxs("mesh", { castShadow: true, receiveShadow: true, children: [
      /* @__PURE__ */ jsx("boxGeometry", { args: [phoneSize.width, phoneSize.height, phoneSize.depth] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color,
          metalness: material === "metallic" ? 0.9 : material === "glass" ? 0.3 : 0.1,
          roughness: material === "glossy" ? 0.1 : material === "glass" ? 0.05 : 0.8,
          transparent: material === "glass",
          opacity: material === "glass" ? 0.95 : 1
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { position: [0, 0, phoneSize.depth / 2 + 1e-3], children: [
      /* @__PURE__ */ jsx("boxGeometry", { args: [borderWidth, borderHeight, 0.02] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: screenBorderColor === "match" ? color : screenBorderColor,
          metalness: 0.5,
          roughness: 0.2
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("mesh", { position: [0, 0, phoneSize.depth / 2 + 0.01], children: [
      /* @__PURE__ */ jsx("boxGeometry", { args: [screenWidth, screenHeight, 0.01] }),
      /* @__PURE__ */ jsx(
        "meshStandardMaterial",
        {
          color: "#111111",
          metalness: 0.5,
          roughness: 0.2
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("group", { position: [
      phoneSize.width / 2 * cameraPosition.x,
      phoneSize.height / 2 * cameraPosition.y,
      -phoneSize.depth / 2
    ], children: [
      cameraStyle === "pill" && /* @__PURE__ */ jsxs("mesh", { position: [0, 0, 0.02], children: [
        /* @__PURE__ */ jsx("capsuleGeometry", { args: [0.15, 0.3, 8, 16] }),
        /* @__PURE__ */ jsx("primitive", { object: cameraMaterial })
      ] }),
      cameraStyle === "circle" && /* @__PURE__ */ jsxs("mesh", { position: [0, 0, 0.01], rotation: [Math.PI / 2, 0, 0], children: [
        /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.15, 0.15, 0.02, 32] }),
        /* @__PURE__ */ jsx("primitive", { object: cameraMaterial })
      ] }),
      cameraStyle === "square" && /* @__PURE__ */ jsxs("mesh", { position: [0, 0, 0], children: [
        /* @__PURE__ */ jsx("boxGeometry", { args: [0.3, 0.3, 0.02] }),
        /* @__PURE__ */ jsx("primitive", { object: cameraMaterial })
      ] }),
      cameraStyle === "triple" && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("mesh", { position: [0, 0.1, 0.01], rotation: [Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.12, 0.12, 0.02, 32] }),
          /* @__PURE__ */ jsx("primitive", { object: cameraMaterial })
        ] }),
        /* @__PURE__ */ jsxs("mesh", { position: [-0.15, -0.05, 0.01], rotation: [Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.08, 0.08, 0.02, 32] }),
          /* @__PURE__ */ jsx("primitive", { object: cameraMaterial })
        ] }),
        /* @__PURE__ */ jsxs("mesh", { position: [0.15, -0.05, 0.01], rotation: [Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.08, 0.08, 0.02, 32] }),
          /* @__PURE__ */ jsx("primitive", { object: cameraMaterial })
        ] }),
        /* @__PURE__ */ jsxs("mesh", { position: [0, -0.15, 5e-3], rotation: [Math.PI / 2, 0, 0], children: [
          /* @__PURE__ */ jsx("cylinderGeometry", { args: [0.05, 0.05, 0.01, 32] }),
          /* @__PURE__ */ jsx("meshStandardMaterial", { color: "#ffeaa7", emissive: "#fdcb6e", emissiveIntensity: 0.2 })
        ] })
      ] })
    ] }),
    hasButtons && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("mesh", { position: [
        phoneSize.width / 2 + 0.01,
        phoneSize.height / 2 * buttonPositions.volume,
        0
      ], children: [
        /* @__PURE__ */ jsx("boxGeometry", { args: [0.05, 0.4, 0.15] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: effectiveButtonColor,
            metalness: material === "metallic" ? 0.9 : material === "glass" ? 0.3 : 0.1,
            roughness: material === "glossy" ? 0.1 : material === "glass" ? 0.05 : 0.8,
            transparent: material === "glass",
            opacity: material === "glass" ? 0.95 : 1
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("mesh", { position: [
        -phoneSize.width / 2 - 0.01,
        phoneSize.height / 2 * buttonPositions.power,
        0
      ], children: [
        /* @__PURE__ */ jsx("boxGeometry", { args: [0.05, 0.3, 0.15] }),
        /* @__PURE__ */ jsx(
          "meshStandardMaterial",
          {
            color: effectiveButtonColor,
            metalness: material === "metallic" ? 0.9 : material === "glass" ? 0.3 : 0.1,
            roughness: material === "glossy" ? 0.1 : material === "glass" ? 0.05 : 0.8,
            transparent: material === "glass",
            opacity: material === "glass" ? 0.95 : 1
          }
        )
      ] })
    ] }),
    logo.type !== "none" && /* @__PURE__ */ jsxs(
      "mesh",
      {
        position: [
          logo.position?.x || 0,
          logo.position?.y || 0,
          -phoneSize.depth / 2 - 1e-3
        ],
        rotation: [0, 0, logo.rotation || 0],
        scale: [
          (logo.scale || 0.2) * phoneSize.width,
          (logo.scale || 0.2) * phoneSize.height,
          1
        ],
        children: [
          /* @__PURE__ */ jsx("planeGeometry", { args: [1, 1] }),
          /* @__PURE__ */ jsx(
            "meshStandardMaterial",
            {
              map: logoTexture,
              color: logo.type === "ai" ? logo.color : "#ffffff",
              transparent: true,
              opacity: 0.8
            }
          )
        ]
      }
    )
  ] });
}
export {
  PhoneModel
};
//# sourceMappingURL=index.js.map